import {
  BREAKPOINTS_V3,
  useHeaderContext,
  useMediaQuery,
} from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { useInView } from 'framer-motion';
import {
  type ComponentPropsWithoutRef,
  type PropsWithChildren,
  type SourceHTMLAttributes,
  useEffect,
  useRef,
} from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import Image from '@/app/components/Image';
import ArtDirectionImage from '@/app/components/Image/ArtDirectionImage';

export enum HeroCarouselSlideVariant {
  FullBleed = 'full-bleed',
  RingFront = 'ring-front',
  RingAngle = 'ring-angle',
}

type SlideWrapperProps = ComponentPropsWithoutRef<'div'> & PropsWithChildren;

interface BaseSlideProps {
  readonly alt: MessageKey;
}

interface FullBleedSlideProps extends BaseSlideProps {
  readonly srcSet: SourceHTMLAttributes<HTMLSourceElement>[];
}

interface RingFrontSlideProps extends BaseSlideProps {
  readonly src: string;
}

interface RingAngleSlideProps extends BaseSlideProps {
  readonly bgColor: string;
  readonly src: string;
}

type FullBleedVariant = {
  readonly variant: HeroCarouselSlideVariant.FullBleed;
} & FullBleedSlideProps;

type RingFrontVariant = {
  readonly variant: HeroCarouselSlideVariant.RingFront;
} & RingFrontSlideProps;

type RingAngleVariant = {
  readonly variant: HeroCarouselSlideVariant.RingAngle;
} & RingAngleSlideProps;

type SlideContentsProps =
  | FullBleedVariant
  | RingFrontVariant
  | RingAngleVariant;

export interface HeroCarouselSlideProps
  extends ComponentPropsWithoutRef<'div'> {
  readonly contents: SlideContentsProps;
  readonly headerInverse?: boolean;
}

const SlideWrapper = ({ className, children, ...props }: SlideWrapperProps) => {
  return (
    <div
      className={cx('absolute inset-0', className)}
      data-cy="hero-carousel-slide"
      {...props}
    >
      {children}
    </div>
  );
};

const FullBleedSlide = ({ alt, srcSet }: FullBleedSlideProps) => {
  return (
    <SlideWrapper data-cy="full-bleed-slide">
      <ArtDirectionImage
        className="absolute size-full object-cover object-center"
        fill
        localizedAlt={alt}
        priority
        srcSet={srcSet}
      />
    </SlideWrapper>
  );
};

const RingFrontSlide = ({ alt, src }: RingFrontSlideProps) => {
  return (
    <SlideWrapper className="bg-[#E4E0DB]" data-cy="ring-front-slide">
      <div className="absolute top-[42.5%] left-1/2 aspect-square w-[315px] max-w-[55%] -translate-1/2 md:top-1/2 xl:w-[520px]">
        <Image fill localizedAlt={alt} priority src={src} />
      </div>
    </SlideWrapper>
  );
};

const RingAngleSlide = ({ alt, bgColor, src }: RingAngleSlideProps) => {
  return (
    <SlideWrapper
      data-cy="ring-angle-slide"
      style={{ backgroundColor: bgColor }}
    >
      <div className="absolute top-[42.5%] left-1/2 aspect-[11/10] w-[315px] -translate-1/2 md:top-1/2 xl:w-[520px]">
        <Image fill localizedAlt={alt} priority src={src} />
      </div>
    </SlideWrapper>
  );
};

const SlideContents = ({ alt, ...props }: SlideContentsProps) => {
  const baseProps: BaseSlideProps = { alt };
  switch (props.variant) {
    case HeroCarouselSlideVariant.FullBleed: {
      const { srcSet } = props;
      return <FullBleedSlide {...baseProps} srcSet={srcSet} />;
    }
    case HeroCarouselSlideVariant.RingFront: {
      const { src } = props;
      return <RingFrontSlide {...baseProps} src={src} />;
    }
    case HeroCarouselSlideVariant.RingAngle: {
      const { bgColor, src } = props;
      return <RingAngleSlide {...baseProps} bgColor={bgColor} src={src} />;
    }
  }
};

const HeroCarouselSlide = ({
  contents,
  headerInverse = false,
  ...props
}: HeroCarouselSlideProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { setInverse } = useHeaderContext()!;
  const isInView = useInView(wrapperRef, { amount: 0.9 });
  const isMobileBreakpoint = useMediaQuery(
    `(max-width: ${BREAKPOINTS_V3.medium - 1}px)`,
  );

  useEffect(() => {
    if (isInView && isMobileBreakpoint) {
      setInverse(headerInverse);
    } else {
      setInverse(false);
    }
  }, [headerInverse, isInView, isMobileBreakpoint, setInverse]);

  return (
    <div className="relative size-full" ref={wrapperRef} {...props}>
      <SlideContents {...contents} />
    </div>
  );
};

export default HeroCarouselSlide;
